import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { X, Loader2, Info } from 'lucide-react'
import { SyncDesktopIcon } from '@/components/icons'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  usePushTestPlanToDevOps,
  useAzureDevOpsProjects,
  useAzureDevOpsTestPlans,
  useAzureDevOpsTestSuites,
} from '@/hooks/useTestPlans'
import { usePushTestSuiteToDevOps } from '@/hooks/useTestSuites'
import { usePushTestCaseToDevOps, usePushTestCasesToDevOps } from '@/hooks/useTestCases'
import { useCurrentUser } from '@/hooks/useAuth'
import { getErrorMessage } from '@/api/errors'
import type { FailedTestItem, PushTestItemsResponse, TestPushConflictPolicy } from '@/types'

export type PushTestItemMode = 'plan' | 'suite' | 'case' | 'cases'

export interface PushTestCaseSummary {
  id: string
  identifier?: string
  title: string
  statusBadge?: ReactNode
}

interface PushTestItemBaseProps {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onPushed?: (result: PushTestItemsResponse) => void
}

interface PushSingleTestItemProps extends PushTestItemBaseProps {
  mode: 'plan' | 'suite' | 'case'
  itemId: string
  itemTitle: string
  itemIdentifier?: string
  itemSubtitle?: string
  statusBadge?: ReactNode
  items?: never
}

interface PushBulkTestCasesProps extends PushTestItemBaseProps {
  mode: 'cases'
  items: PushTestCaseSummary[]
  itemId?: never
  itemTitle?: never
  itemIdentifier?: never
  itemSubtitle?: never
  statusBadge?: never
}

type PushTestItemToDevOpsDialogProps = PushSingleTestItemProps | PushBulkTestCasesProps

const MODE_LABELS: Record<PushTestItemMode, string> = {
  plan: 'Test Plan',
  suite: 'Test Suite',
  case: 'Test Case',
  cases: 'Test Cases',
}

const MAX_FAILURE_REASONS = 5

function failureReasons(failed: FailedTestItem[]): ReactNode {
  const reasons = [...new Set(failed.map(item => item.errorMessage).filter(Boolean))]

  if (reasons.length === 0) return undefined

  const shown = reasons.slice(0, MAX_FAILURE_REASONS)
  const remaining = reasons.length - shown.length

  return (
    <ul className="list-disc space-y-0.5 pl-4" data-testid="push-test-item-failure-reasons">
      {shown.map(reason => <li key={reason}>{reason}</li>)}
      {remaining > 0 && <li key="__more" className="list-none">…and {remaining} more</li>}
    </ul>
  )
}

function reportResult(
  result: PushTestItemsResponse,
  itemLabel?: { singular: string; plural: string },
) {
  const label = itemLabel ?? { singular: 'item', plural: 'items' }
  const pushed = result.pushed.length
  const skipped = result.skipped.length
  const failed = result.failed.length
  const created = result.pushed.filter(p => p.action === 'create').length
  const updated = result.pushed.filter(p => p.action === 'update').length
  const description = failureReasons(result.failed)
  const noun = (count: number) => (count === 1 ? label.singular : label.plural)

  if (result.dryRun) {
    if (failed > 0) {
      toast.error(`Dry run found ${failed} problem${failed === 1 ? '' : 's'}`, { description })
    } else {
      const skipNote = skipped ? `, ${skipped} to skip` : ''
      toast.info(`Dry run — ${created} to create, ${updated} to update${skipNote}. No changes made.`)
    }
    return
  }

  const outcomes = [
    created ? `${created} created` : null,
    updated ? `${updated} updated` : null,
    skipped ? `${skipped} skipped` : null,
  ].filter(Boolean).join(', ')

  if (failed === 0) {
    const detail = outcomes ? ` (${outcomes})` : ''
    toast.success(`Pushed ${pushed} ${noun(pushed)} to Azure DevOps${detail}`)
  } else if (pushed === 0 && skipped === 0) {
    toast.error(`Failed to push ${failed} ${noun(failed)} to Azure DevOps`, { description })
  } else {
    const parts = outcomes ? `${outcomes}, ${failed} failed` : `${failed} failed`
    toast.warning(`Pushed ${pushed} of ${pushed + skipped + failed} ${label.plural}: ${parts}`, { description })
  }
}

export function PushTestItemToDevOpsDialog(props: PushTestItemToDevOpsDialogProps) {
  const { projectId, mode, open, onOpenChange, onPushed } = props
  const [selectedProject, setSelectedProject] = useState<string>('')
  const [selectedPlanId, setSelectedPlanId] = useState<string>('')
  const [selectedSuiteId, setSelectedSuiteId] = useState<string>('')
  const [conflictPolicy, setConflictPolicy] = useState<TestPushConflictPolicy>('UpdateInPlace')
  const [dryRun, setDryRun] = useState(false)
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const tagInputRef = useRef<HTMLInputElement>(null)

  const needsPlan = mode !== 'plan'
  const needsSuite = mode === 'case' || mode === 'cases'
  const supportsOptions = mode !== 'plan'

  const planIdNumber = selectedPlanId ? Number(selectedPlanId) : null

  const { data: currentUser } = useCurrentUser()
  const publisherName = currentUser?.displayName ?? currentUser?.email ?? 'current user'

  const projectsQuery = useAzureDevOpsProjects(projectId, open)
  const plansQuery = useAzureDevOpsTestPlans(projectId, open && needsPlan ? selectedProject : '')
  const suitesQuery = useAzureDevOpsTestSuites(projectId, open && needsSuite ? selectedProject : '', open && needsSuite ? planIdNumber : null)

  const pushPlan = usePushTestPlanToDevOps(projectId)
  const pushSuite = usePushTestSuiteToDevOps(projectId)
  const pushCase = usePushTestCaseToDevOps(projectId)
  const pushCases = usePushTestCasesToDevOps(projectId)

  const isPending = pushPlan.isPending || pushSuite.isPending || pushCase.isPending || pushCases.isPending

  // Reset selection whenever the dialog re-opens.
  useEffect(() => {
    if (open) {
      setSelectedProject('')
      setSelectedPlanId('')
      setSelectedSuiteId('')
      setConflictPolicy('UpdateInPlace')
      setDryRun(false)
      setTags([])
      setTagInput('')
    }
  }, [open])

  // Preselect the project when the connector exposes exactly one.
  useEffect(() => {
    const projects = projectsQuery.data
    if (open && !selectedProject && projects?.length === 1) {
      setSelectedProject(projects[0].name)
    }
  }, [open, selectedProject, projectsQuery.data])

  // Changing the project invalidates the chosen plan/suite.
  useEffect(() => {
    setSelectedPlanId('')
    setSelectedSuiteId('')
  }, [selectedProject])

  // Changing the plan invalidates the chosen suite.
  useEffect(() => {
    setSelectedSuiteId('')
  }, [selectedPlanId])

  const addTag = () => {
    const trimmed = tagInput.trim()
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed])
    }
    setTagInput('')
  }

  const removeTag = (tag: string) => setTags(tags.filter(t => t !== tag))

  const selectedPlan = plansQuery.data?.find(p => p.id === planIdNumber)
  const selectedSuiteName = suitesQuery.data?.find(s => s.id === Number(selectedSuiteId))?.name

  const destination = useMemo(() => {
    if (!selectedProject) return []
    const parts = [selectedProject]
    if (needsPlan && selectedPlan) parts.push(selectedPlan.name)
    if (needsSuite && selectedSuiteName) parts.push(selectedSuiteName)
    return parts
  }, [selectedProject, needsPlan, selectedPlan, needsSuite, selectedSuiteName])

  const canConfirm = (() => {
    if (isPending || !selectedProject) return false
    if (props.mode === 'cases' && props.items.length === 0) return false
    if (needsPlan && !planIdNumber) return false
    if (needsSuite && !selectedSuiteId) return false
    return true
  })()

  const handlePush = async () => {
    try {
      let result: PushTestItemsResponse

      if (props.mode === 'plan') {
        result = await pushPlan.mutateAsync({ testPlanId: props.itemId, data: { targetAzureProject: selectedProject, tags } })
      } else if (props.mode === 'suite') {
        result = await pushSuite.mutateAsync({
          testSuiteId: props.itemId,
          data: { targetAzureProject: selectedProject, targetAzurePlanId: planIdNumber!, conflictPolicy, dryRun, tags },
        })
      } else if (props.mode === 'cases') {
        result = await pushCases.mutateAsync({
          testCaseIds: props.items.map(i => i.id),
          targetAzureProject: selectedProject,
          targetAzurePlanId: planIdNumber!,
          targetAzureSuiteId: Number(selectedSuiteId),
          conflictPolicy,
          dryRun,
          tags,
        })
      } else {
        result = await pushCase.mutateAsync({
          testCaseId: props.itemId,
          data: { targetAzureProject: selectedProject, targetAzurePlanId: planIdNumber!, targetAzureSuiteId: Number(selectedSuiteId), conflictPolicy, dryRun, tags },
        })
      }

      const usesTestCaseLabel = mode === 'case' || mode === 'cases'
      reportResult(result, usesTestCaseLabel ? { singular: 'test case', plural: 'test cases' } : undefined)

      if (!result.dryRun) {
        onOpenChange(false)
        onPushed?.(result)
      }
    } catch (error) {
      toast.error('Failed to push to Azure DevOps', { description: getErrorMessage(error) })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]" aria-describedby={undefined} data-testid="push-test-item-to-devops-dialog">
        <DialogHeader>
          <DialogTitle>Push {MODE_LABELS[mode]} to Azure DevOps</DialogTitle>
        </DialogHeader>

        <div className="min-w-0 space-y-5">
          {props.mode === 'cases' ? (
            <div className="rounded-lg border" data-testid="push-test-cases-summary">
              <p className="border-b px-3 py-2 text-sm font-medium">
                {props.items.length} test case{props.items.length === 1 ? '' : 's'} will be pushed:
              </p>
              <ul className="max-h-32 overflow-y-auto">
                {props.items.map(item => (
                  <li
                    key={item.id}
                    className="flex items-center gap-3 border-b px-3 py-2 last:border-b-0"
                    data-testid={`push-test-cases-summary-item-${item.identifier ?? item.id}`}
                  >
                    {item.identifier && (
                      <span className="shrink-0 text-sm font-semibold text-muted-foreground">{item.identifier}</span>
                    )}
                    <span className="min-w-0 flex-1 truncate text-sm" title={item.title}>{item.title}</span>
                    {item.statusBadge && <div className="shrink-0">{item.statusBadge}</div>}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="flex items-center gap-4 rounded-lg border p-3">
              {props.itemIdentifier && (
                <span className="shrink-0 text-sm font-semibold text-muted-foreground">{props.itemIdentifier}</span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium" title={props.itemTitle}>{props.itemTitle}</p>
                {props.itemSubtitle && <p className="text-xs text-muted-foreground">{props.itemSubtitle}</p>}
              </div>
              {props.statusBadge && <div className="shrink-0">{props.statusBadge}</div>}
            </div>
          )}

          <div className="space-y-4">
            <h3 className="text-base font-bold">Destination in Azure DevOps</h3>

            {/* Azure DevOps Project */}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium" htmlFor="ado-project-select">
                Azure DevOps Project <span className="text-red-500">*</span>
              </Label>
              {projectsQuery.isLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading projects...
                </div>
              ) : (
                <Select value={selectedProject} onValueChange={setSelectedProject}>
                  <SelectTrigger id="ado-project-select" data-testid="ado-project-select" aria-label="Azure DevOps Project">
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {(projectsQuery.data ?? []).map(p => (
                      <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {needsPlan && (
              <div className="flex gap-4">
                {/* Target Test Plan (suite + case modes) */}
                <div className="flex-1 space-y-1.5">
                  <Label className="text-sm font-medium" htmlFor="ado-plan-select">
                    Target Test Plan <span className="text-red-500">*</span>
                  </Label>
                  {plansQuery.isLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                    </div>
                  ) : (
                    <Select value={selectedPlanId} onValueChange={setSelectedPlanId} disabled={!selectedProject}>
                      <SelectTrigger id="ado-plan-select" data-testid="ado-plan-select" aria-label="Target Test Plan">
                        <SelectValue placeholder="Select a test plan" />
                      </SelectTrigger>
                      <SelectContent>
                        {(plansQuery.data ?? []).map(plan => (
                          <SelectItem key={plan.id} value={String(plan.id)}>{plan.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Target Test Suite (case mode only) */}
                {needsSuite && (
                  <div className="flex-1 space-y-1.5">
                    <Label className="text-sm font-medium" htmlFor="ado-suite-select">
                      Target Test Suite <span className="text-red-500">*</span>
                    </Label>
                    {suitesQuery.isLoading ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                      </div>
                    ) : (
                      <Select value={selectedSuiteId} onValueChange={setSelectedSuiteId} disabled={!planIdNumber}>
                        <SelectTrigger id="ado-suite-select" data-testid="ado-suite-select" aria-label="Target Test Suite">
                          <SelectValue placeholder="Select a test suite" />
                        </SelectTrigger>
                        <SelectContent>
                          {(suitesQuery.data ?? []).map(suite => (
                            <SelectItem key={suite.id} value={String(suite.id)}>
                              {suite.parentSuiteId == null ? `${suite.name} (plan root)` : suite.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
              </div>
            )}

            {destination.length > 0 && (
              <div className="flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground" data-testid="push-destination-banner">
                <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>
                  Items will be created under{' '}
                  {destination.map((part, i) => (
                    <span key={`${i}-${part}`}>
                      {i > 0 && ' → '}
                      <span className="font-semibold text-foreground">{part}</span>
                    </span>
                  ))}
                  . An IDSP back-reference is added to each work item description for traceability.
                </span>
              </div>
            )}
          </div>

          {/* Options (case & suite) */}
          {supportsOptions && (
            <div className="space-y-1.5">
              <Label className="text-sm font-medium" htmlFor="ado-conflict-select">If an item already exists in Azure DevOps</Label>
              <Select value={conflictPolicy} onValueChange={(value) => setConflictPolicy(value as TestPushConflictPolicy)}>
                <SelectTrigger id="ado-conflict-select" data-testid="ado-conflict-select" aria-label="If an item already exists in Azure DevOps">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CreateNew">Create new</SelectItem>
                  <SelectItem value="UpdateInPlace">Update in place</SelectItem>
                  <SelectItem value="Skip">Skip</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Tags */}
          <div className="space-y-1.5">
            <Label htmlFor="ado-tags-input" className="block text-base font-bold">Tags</Label>
            <div
              className="flex min-h-10 w-full flex-wrap items-center gap-1 rounded-md border border-border bg-background px-2 py-1.5 text-sm ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 cursor-text"
              onClick={() => tagInputRef.current?.focus()}
            >
              {tags.map(tag => (
                <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                  {tag}
                  <button
                    type="button"
                    aria-label={`Remove tag ${tag}`}
                    onClick={(e) => { e.stopPropagation(); removeTag(tag) }}
                    className="rounded-full outline-none focus:ring-2 focus:ring-ring"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <input
                ref={tagInputRef}
                id="ado-tags-input"
                aria-label="Tags"
                data-testid="ado-tags-input"
                value={tagInput}
                placeholder={tags.length === 0 ? 'Type a tag and press Enter' : ''}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addTag()
                  } else if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
                    removeTag(tags[tags.length - 1])
                  }
                }}
                onBlur={addTag}
                className="min-w-[120px] flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
              />
            </div>
          </div>

          {/* Dry run (case & suite) */}
          {supportsOptions && (
            <Label className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={dryRun}
                onCheckedChange={(value) => setDryRun(value === true)}
                data-testid="ado-dry-run"
              />
              <span>Dry run — validate the destination and conflicts without creating anything</span>
            </Label>
          )}
        </div>

        <DialogFooter className="sm:items-center sm:justify-between">
          <span className="text-xs text-muted-foreground">Publish as {publisherName}</span>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              type="button"
              onClick={handlePush}
              disabled={!canConfirm}
              data-testid="push-test-item-confirm"
            >
              <SyncDesktopIcon className="h-4 w-4" />
              {isPending ? (dryRun ? 'Checking...' : 'Pushing...') : (dryRun ? 'Run dry run' : 'Push to Azure DevOps')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
